import { useMemo } from 'react';
import type {
  BillingRatesSummary,
  BillingRules,
  BomItem,
  JobExpense,
  MaintenanceRecord,
  ProjectExpenseSummary,
  ProjectTimeSummary,
  TeamScopeSnapshot,
  TimeSummary,
} from './billingDashboardTypes';

export function useBillingCalculations(params: {
  bomItems: BomItem[];
  jobExpenses: JobExpense[];
  projectExpenseSummary: ProjectExpenseSummary | null;
  maintenanceRecords: MaintenanceRecord[];
  billingRates: BillingRatesSummary;
  billingRules: BillingRules;
  timeSummary: TimeSummary | null;
  projectTimeSummary: ProjectTimeSummary | null;
  teamScopeSnapshot: TeamScopeSnapshot | null;
}) {
  const {
    bomItems,
    jobExpenses,
    projectExpenseSummary,
    maintenanceRecords,
    billingRates,
    billingRules,
    timeSummary,
    projectTimeSummary,
    teamScopeSnapshot,
  } = params;

  const billableHours = projectTimeSummary?.billable_hours ?? timeSummary?.billable_duration_hours ?? 0;
  const totalHours = projectTimeSummary?.total_hours ?? timeSummary?.total_duration_hours ?? 0;
  const travelHours = projectTimeSummary?.travel_hours ?? timeSummary?.travel_hours ?? 0;
  const travelKm = projectTimeSummary?.travel_distance_km ?? timeSummary?.travel_distance_km ?? 0;
  const onsiteHours = projectTimeSummary?.onsite_hours ?? timeSummary?.onsite_hours ?? Math.max(totalHours - travelHours, 0);
  const relatedItems = projectTimeSummary?.jobs_count ?? timeSummary?.related_items_count ?? 0;

  const billableLinesTotal = useMemo(() => (
    bomItems.reduce((sum, item) => {
      const explicitTotal = Number(item.line_total ?? item.total_cost ?? 0);
      if (explicitTotal > 0) {
        return sum + explicitTotal;
      }
      return sum + ((Number(item.quantity) || 0) * (Number(item.unit_cost) || 0));
    }, 0)
  ), [bomItems]);

  const expensesTotal = useMemo(() => {
    if (projectExpenseSummary) {
      return Number(projectExpenseSummary.total_amount) || 0;
    }

    return jobExpenses.reduce((sum, expense) => sum + (Number(expense.grand_total ?? expense.amount ?? 0) || 0), 0);
  }, [jobExpenses, projectExpenseSummary]);

  const maintenanceTotal = useMemo(() => (
    maintenanceRecords.reduce((sum, record) => sum + (Number(record.cost) || 0), 0)
  ), [maintenanceRecords]);

  const effectiveLabourRate = useMemo(() => {
    return billingRates.labour_rate || 0;
  }, [billingRates.labour_rate]);

  const labourRecoveryEstimate = useMemo(() => {
    if (!billingRules.is_synced) return 0;
    return onsiteHours * effectiveLabourRate;
  }, [billingRules.is_synced, onsiteHours, effectiveLabourRate]);

  const travelTimeRecoveryEstimate = useMemo(() => {
    if (!billingRules.is_synced) return 0;
    const chargeableHours = billingRules.min_travel_time_hours > 0
      ? Math.max(0, travelHours - billingRules.min_travel_time_hours)
      : travelHours;
    return chargeableHours * (billingRates.travel_time_rate || 0);
  }, [billingRules.is_synced, billingRules.min_travel_time_hours, travelHours, billingRates.travel_time_rate]);

  const travelDistanceRecoveryEstimate = useMemo(() => {
    if (!billingRules.is_synced) return 0;
    const chargeableKm = billingRules.min_travel_distance_km > 0
      ? Math.max(0, travelKm - billingRules.min_travel_distance_km)
      : travelKm;
    return chargeableKm * (billingRates.travel_km_rate || 0);
  }, [billingRules.is_synced, billingRules.min_travel_distance_km, travelKm, billingRates.travel_km_rate]);

  const travelRecoveryEstimate = useMemo(
    () => travelTimeRecoveryEstimate + travelDistanceRecoveryEstimate,
    [travelDistanceRecoveryEstimate, travelTimeRecoveryEstimate],
  );

  const callOutFeeTotal = useMemo(() => {
    if (!billingRules.is_synced || billingRules.call_out_fee_per_appointment <= 0) return 0;
    const appointmentCount = teamScopeSnapshot?.total_appointments || 1;
    return billingRules.charge_call_out_per_appointment
      ? billingRules.call_out_fee_per_appointment * appointmentCount
      : billingRules.call_out_fee_per_appointment;
  }, [billingRules.is_synced, billingRules.call_out_fee_per_appointment, billingRules.charge_call_out_per_appointment, teamScopeSnapshot?.total_appointments]);

  const recoverablesTotal = useMemo(
    () => billableLinesTotal + expensesTotal + maintenanceTotal + labourRecoveryEstimate + travelRecoveryEstimate + callOutFeeTotal,
    [billableLinesTotal, expensesTotal, maintenanceTotal, labourRecoveryEstimate, travelRecoveryEstimate, callOutFeeTotal],
  );

  return {
    billableHours,
    totalHours,
    travelHours,
    travelKm,
    onsiteHours,
    relatedItems,
    billableLinesTotal,
    expensesTotal,
    maintenanceTotal,
    effectiveLabourRate,
    labourRecoveryEstimate,
    travelTimeRecoveryEstimate,
    travelDistanceRecoveryEstimate,
    travelRecoveryEstimate,
    callOutFeeTotal,
    recoverablesTotal,
  };
}
